"use strict";
import { TradeType, CurrencyAmount, Token, Percent, Price } from "@uniswap/sdk-core";
import { Pair, Route as V2Route } from "@uniswap/v2-sdk";
import { Protocol } from "@uniswap/router-sdk";
import { Contract, ethers } from "ethers";
import { ClassicTrade, QuoteMethod } from "state/routing/types";
import { TICK_SPACING_TO_FEE } from "constants/bscContracts";
import { getCommonIntermediaries } from "features/routing/constants/intermediaries";
import { routingLog, routingWarn, routingError } from "features/routing/utils/debug";
import { V2_ROUTER_ABI } from "features/routing/quoter/bscV2Quoter";
function calculatePriceImpact(currencyIn, currencyOut, inputAmount, outputAmount, poolReserve0, poolReserve1, tokenInIsToken0) {
  try {
    if (poolReserve0 && poolReserve1 && tokenInIsToken0 !== void 0) {
      const inputReserve = tokenInIsToken0 ? parseFloat(poolReserve0) : parseFloat(poolReserve1);
      const outputReserve = tokenInIsToken0 ? parseFloat(poolReserve1) : parseFloat(poolReserve0);
      if (inputReserve > 0 && outputReserve > 0) {
        const inputAmountFloat = parseFloat(inputAmount.toFixed(6));
        const _outputAmountFloat = parseFloat(outputAmount.toFixed(6));
        const priceImpactPercent = inputAmountFloat / (inputReserve + inputAmountFloat) * 100;
        const basisPoints = Math.floor(priceImpactPercent * 100);
        return new Percent(basisPoints, 1e4);
      }
    }
    const _executionPrice = new Price(
      currencyIn,
      currencyOut,
      inputAmount.quotient,
      outputAmount.quotient
    );
    return new Percent(30, 1e4);
  } catch (error) {
    routingWarn("Error calculating price impact", error);
    return new Percent(0);
  }
}
function parseReserveToRaw(reserve, decimals) {
  if (reserve.includes("e") || reserve.includes("E")) {
    try {
      const num = parseFloat(reserve);
      if (!isFinite(num) || num < 0) {
        routingWarn("Invalid scientific notation reserve", reserve);
        return "0";
      }
      const fixedStr = num.toFixed(decimals);
      return parseReserveToRaw(fixedStr, decimals);
    } catch (error) {
      routingWarn("Failed to parse scientific notation", { reserve, error });
      return "0";
    }
  }
  const parts = reserve.split(".");
  const wholePart = parts[0] || "0";
  const fractionalPart = parts[1] || "";
  const fractionalDigits = fractionalPart.length;
  const zerosToAdd = Math.max(0, decimals - fractionalDigits);
  const rawAmount = wholePart + fractionalPart + "0".repeat(zerosToAdd);
  return rawAmount.replace(/^0+/, "") || "0";
}
export async function getV2Quote(params) {
  const { currencyIn, currencyOut, inputAmount, tradeType, poolData, effectiveChainId, routerAddress, factoryAddress, ethersProvider } = params;
  try {
    if (tradeType !== TradeType.EXACT_INPUT) {
      return null;
    }
    const tokenInAddress = (currencyIn.isToken ? currencyIn.address : currencyIn.wrapped.address).toLowerCase();
    const tokenOutAddress = (currencyOut.isToken ? currencyOut.address : currencyOut.wrapped.address).toLowerCase();
    const keyFor = (a, b, stable) => `${a}-${b}-${stable ? "s" : "v"}`;
    const poolMap = /* @__PURE__ */ new Map();
    for (const p of poolData) {
      const a = p.token0.id.toLowerCase();
      const b = p.token1.id.toLowerCase();
      const s = Boolean(p.stable);
      poolMap.set(keyFor(a, b, s), p);
      poolMap.set(keyFor(b, a, s), p);
    }
    const makePair = (p) => {
      try {
        const t0 = new Token(effectiveChainId, p.token0.id, parseInt(p.token0.decimals), p.token0.symbol, p.token0.name);
        const t1 = new Token(effectiveChainId, p.token1.id, parseInt(p.token1.decimals), p.token1.symbol, p.token1.name);
        const r0 = parseReserveToRaw(p.reserve0, t0.decimals);
        const r1 = parseReserveToRaw(p.reserve1, t1.decimals);
        if (r0 === "0" || r1 === "0") return null;
        const ca0 = CurrencyAmount.fromRawAmount(t0, r0);
        const ca1 = CurrencyAmount.fromRawAmount(t1, r1);
        const pair = new Pair(ca0, ca1);
        pair.actualAddress = p.id;
        pair.stable = Boolean(p.stable);
        return pair;
      } catch (err) {
        routingError("V2 Pair Debug: Failed to create pair", err);
        return null;
      }
    };
    const candidates = [];
    const seen = /* @__PURE__ */ new Set();
    const addCandidate = (path, stables, pools) => {
      const key = `${path.join(">")}|${stables.map((s) => s ? "s" : "v").join(">")}`;
      if (seen.has(key)) return;
      const pairs = [];
      for (const p of pools) {
        const pr = makePair(p);
        if (!pr) return;
        pairs.push(pr);
      }
      const hops = [];
      for (let i = 0; i < path.length - 1; i++) {
        hops.push({ from: path[i], to: path[i + 1], stable: stables[i], factory: factoryAddress });
      }
      candidates.push({ path, stableFlags: stables, hops, pairs });
      seen.add(key);
    };
    for (const s of [false, true]) {
      const p = poolMap.get(keyFor(tokenInAddress, tokenOutAddress, s));
      if (p) addCandidate([tokenInAddress, tokenOutAddress], [s], [p]);
    }
    const connectors = getCommonIntermediaries(effectiveChainId).map((x) => x.toLowerCase()).filter((x) => x !== tokenInAddress && x !== tokenOutAddress);
    for (const mid of connectors) {
      for (const s1 of [false, true]) {
        for (const s2 of [false, true]) {
          const p1 = poolMap.get(keyFor(tokenInAddress, mid, s1));
          const p2 = poolMap.get(keyFor(mid, tokenOutAddress, s2));
          if (p1 && p2) addCandidate([tokenInAddress, mid, tokenOutAddress], [s1, s2], [p1, p2]);
        }
      }
    }
    if (connectors.length > 1) {
      for (let i = 0; i < connectors.length; i++) {
        const mid1 = connectors[i];
        for (let j = 0; j < connectors.length; j++) {
          const mid2 = connectors[j];
          if (mid1 === mid2) continue;
          if (mid1 === tokenInAddress || mid1 === tokenOutAddress) continue;
          if (mid2 === tokenInAddress || mid2 === tokenOutAddress) continue;
          for (const s1 of [false, true]) {
            for (const s2 of [false, true]) {
              for (const s3 of [false, true]) {
                const p1 = poolMap.get(keyFor(tokenInAddress, mid1, s1));
                const p2 = poolMap.get(keyFor(mid1, mid2, s2));
                const p3 = poolMap.get(keyFor(mid2, tokenOutAddress, s3));
                if (p1 && p2 && p3) {
                  addCandidate(
                    [tokenInAddress, mid1, mid2, tokenOutAddress],
                    [s1, s2, s3],
                    [p1, p2, p3]
                  );
                }
              }
            }
          }
        }
      }
    }
    if (candidates.length === 0) {
      return null;
    }
    const router = new Contract(routerAddress, V2_ROUTER_ABI, ethersProvider);
    const amountInRaw = inputAmount.quotient.toString();
    let results = [];
    const multicall = params.multicallContract;
    if (multicall && candidates.length > 1) {
      const calls = candidates.map((c) => {
        const cd = router.interface.encodeFunctionData("getAmountsOut", [amountInRaw, c.hops]);
        return { target: router.address, callData: cd };
      });
      try {
        const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error("Multicall timeout after 30s")), 3e4));
        const multicallPromise = multicall.callStatic.tryAggregate(false, calls);
        const res = await Promise.race([multicallPromise, timeoutPromise]);
        res.forEach((r, i) => {
          if (!r?.success) {
            results.push({ c: candidates[i], out: "0" });
            return;
          }
          try {
            const decoded = router.interface.decodeFunctionResult("getAmountsOut", r.returnData);
            const amounts = decoded[0];
            const out = amounts[amounts.length - 1].toString();
            results.push({ c: candidates[i], out });
          } catch (e) {
            routingWarn("Failed to decode V2 multicall getAmountsOut result", e);
            results.push({ c: candidates[i], out: "0" });
          }
        });
      } catch (e) {
        routingWarn("V2 multicall batch failed, falling back to parallel direct calls", e);
      }
    }
    if (results.length === 0) {
      results = await Promise.all(
        candidates.map(async (c) => {
          try {
            const amounts = await router.getAmountsOut(amountInRaw, c.hops);
            const out = amounts[amounts.length - 1].toString();
            return { c, out };
          } catch (e) {
            routingWarn("V2 direct getAmountsOut call failed for candidate", e);
            return { c, out: "0" };
          }
        })
      );
    }
    let best = null;
    for (const r of results) {
      if (!best || ethers.BigNumber.from(r.out).gt(ethers.BigNumber.from(best.out))) best = r;
    }
    if (!best || best.out === "0") {
      return null;
    }
    return {
      path: best.c.path,
      stableFlags: best.c.stableFlags,
      pairs: best.c.pairs,
      inputAmountRaw: amountInRaw,
      outputAmountRaw: best.out
    };
  } catch (error) {
    routingError("V2 quote error", error);
    return null;
  }
}
const v3QuoteCache = /* @__PURE__ */ new Map();
const CACHE_TTL_MS = 15e3;
function createV3CacheKey(params) {
  return `${params.tokenIn.toLowerCase()}-${params.tokenOut.toLowerCase()}-${params.amountSpecified}-${params.tradeType}`;
}
function cleanExpiredCache() {
  const now = Date.now();
  for (const [key, entry] of v3QuoteCache.entries()) {
    if (now - entry.timestamp > CACHE_TTL_MS) {
      v3QuoteCache.delete(key);
    }
  }
}
export async function getV3Quote(params) {
  const { tokenIn, tokenOut, inputAmount, amountSpecified, tradeType, v3Pools, quoterContract, multicallContract, chainId } = params;
  const cacheKey = createV3CacheKey(params);
  cleanExpiredCache();
  const cached = v3QuoteCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
    routingLog("Cache hit for amount", amountSpecified);
    return cached.result;
  }
  try {
    routingLog("Start V3 quote", { tokenIn, tokenOut, tradeType, amountSpecified });
    const tokenInLower = tokenIn.toLowerCase();
    const tokenOutLower = tokenOut.toLowerCase();
    const directV3Pools = v3Pools.filter((pool) => {
      const token0Lower = pool.token0.id.toLowerCase();
      const token1Lower = pool.token1.id.toLowerCase();
      return token0Lower === tokenInLower && token1Lower === tokenOutLower || token0Lower === tokenOutLower && token1Lower === tokenInLower;
    });
    routingLog("Direct V3 pools count", directV3Pools.length);
    const availableTickSpacings = [...new Set(directV3Pools.map((pool) => pool.tickSpacing))];
    routingLog("Direct tickSpacings", availableTickSpacings);
    const calls = availableTickSpacings.map((tickSpacing) => {
      const tsNum = parseInt(tickSpacing);
      if (tradeType === TradeType.EXACT_INPUT) {
        const pathFwd = encodeV3PathTickSpacing([tokenInLower, tokenOutLower], [tsNum]);
        const callData = quoterContract.interface.encodeFunctionData("quoteExactInput", [
          pathFwd,
          inputAmount.quotient.toString()
        ]);
        return { target: quoterContract.address, callData };
      } else {
        const pathRev = encodeV3PathTickSpacing([tokenOutLower, tokenInLower], [tsNum]);
        const callData = quoterContract.interface.encodeFunctionData("quoteExactOutput", [
          pathRev,
          amountSpecified
        ]);
        return { target: quoterContract.address, callData };
      }
    });
    let directResults = [];
    if (calls.length > 0) {
      try {
        const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error("Multicall timeout after 30 seconds")), 3e4));
        const multicallPromise = multicallContract.callStatic.tryAggregate(false, calls);
        directResults = await Promise.race([multicallPromise, timeoutPromise]);
      } catch (e) {
        routingError("V3 direct multicall failed", e);
      }
    }
    let bestDirectQuote = null;
    let bestDirectFee = 0;
    let bestDirectTickSpacing = 0;
    const processedDirect = directResults.map((callResult, i) => {
      const tickSpacing = availableTickSpacings[i];
      const fee = TICK_SPACING_TO_FEE[parseInt(tickSpacing)] ?? 3e3;
      if (!fee || !callResult?.success) return null;
      try {
        const decoded = tradeType === TradeType.EXACT_INPUT ? quoterContract.interface.decodeFunctionResult("quoteExactInput", callResult.returnData) : quoterContract.interface.decodeFunctionResult("quoteExactOutput", callResult.returnData);
        return { result: decoded, fee, tickSpacing };
      } catch (err) {
        routingWarn("Failed to decode direct quote", { tickSpacing, err });
        return null;
      }
    });
    for (const q of processedDirect) {
      if (!q) continue;
      if (tradeType === TradeType.EXACT_INPUT) {
        if (!bestDirectQuote || q.result.amountOut.gt(bestDirectQuote.amountOut)) {
          bestDirectQuote = q.result;
          bestDirectFee = q.fee;
          bestDirectTickSpacing = parseInt(q.tickSpacing);
        }
      } else {
        if (!bestDirectQuote || q.result.amountIn.lt(bestDirectQuote.amountIn)) {
          bestDirectQuote = q.result;
          bestDirectFee = q.fee;
          bestDirectTickSpacing = parseInt(q.tickSpacing);
        }
      }
    }
    let bestMultiHop = null;
    const candidates = buildV3MultiHopCandidates({ v3Pools, tokenIn: tokenInLower, tokenOut: tokenOutLower, chainId });
    routingLog("Multi-hop candidates", candidates);
    if (candidates.length > 0) {
      if (tradeType === TradeType.EXACT_INPUT) {
        const mhCalls = candidates.map((c) => {
          const encodedPath = encodeV3PathTickSpacing(c.path, c.tickSpacings);
          const cd = quoterContract.interface.encodeFunctionData("quoteExactInput", [encodedPath, inputAmount.quotient.toString()]);
          return { target: quoterContract.address, callData: cd };
        });
        try {
          const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error("Multicall timeout after 30 seconds")), 3e4));
          const multicallPromise = multicallContract.callStatic.tryAggregate(false, mhCalls);
          const mhResults = await Promise.race([multicallPromise, timeoutPromise]);
          mhResults.forEach((r, i) => {
            if (!r?.success) return;
            try {
              const decoded = quoterContract.interface.decodeFunctionResult("quoteExactInput", r.returnData);
              const amountOut = decoded[0];
              routingLog("Multi-hop EI quote", { path: candidates[i].path, fees: candidates[i].fees, amountOut: amountOut.toString() });
              if (amountOut && !amountOut.isZero() && (!bestMultiHop || amountOut.gt(bestMultiHop.amountOut))) {
                bestMultiHop = { amountOut, path: candidates[i].path, fees: candidates[i].fees, tickSpacings: candidates[i].tickSpacings };
              }
            } catch (e) {
              routingWarn("Failed to decode V3 multi-hop exact-input quote", { path: candidates[i]?.path, error: e });
            }
          });
        } catch (e) {
          routingError("V3 multi-hop exact input failed", e);
        }
      } else if (tradeType === TradeType.EXACT_OUTPUT) {
        const mhCalls = candidates.map((c) => {
          const revPath = encodeV3PathTickSpacing([...c.path].reverse(), [...c.tickSpacings].reverse());
          const cd = quoterContract.interface.encodeFunctionData("quoteExactOutput", [revPath, amountSpecified]);
          return { target: quoterContract.address, callData: cd };
        });
        try {
          const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error("Multicall timeout after 30 seconds")), 3e4));
          const multicallPromise = multicallContract.callStatic.tryAggregate(false, mhCalls);
          const mhResults = await Promise.race([multicallPromise, timeoutPromise]);
          mhResults.forEach((r, i) => {
            if (!r?.success) return;
            try {
              const decoded = quoterContract.interface.decodeFunctionResult("quoteExactOutput", r.returnData);
              const amountIn = decoded[0];
              routingLog("Multi-hop EO quote", { path: candidates[i].path, fees: candidates[i].fees, amountIn: amountIn.toString(), amountOut: amountSpecified });
              if (amountIn && (!bestMultiHop || amountIn.lt(bestMultiHop.amountIn))) {
                bestMultiHop = { amountIn, path: candidates[i].path, fees: candidates[i].fees, tickSpacings: candidates[i].tickSpacings };
              }
            } catch (e) {
              routingWarn("Failed to decode V3 multi-hop exact-output quote", { path: candidates[i]?.path, error: e });
            }
          });
        } catch (e) {
          routingError("V3 multi-hop exact output failed", e);
        }
      }
    }
    const selectedMultiHop = bestMultiHop;
    let quoteResult = null;
    if (tradeType === TradeType.EXACT_INPUT) {
      const directOut = bestDirectQuote?.amountOut;
      const multiOut = selectedMultiHop?.amountOut;
      if (multiOut && !multiOut.isZero() && (!directOut || multiOut.gt(directOut))) {
        routingLog("Selected best route: multi-hop (EXACT_INPUT)", { path: selectedMultiHop.path, fees: selectedMultiHop.fees, amountOut: multiOut.toString() });
        quoteResult = {
          amountOut: multiOut.toString(),
          amountIn: inputAmount.quotient.toString(),
          fee: 0,
          tickSpacing: 0,
          tokenIn: params.currencyIn,
          tokenOut: params.currencyOut,
          gasEstimate: "200000",
          path: selectedMultiHop.path,
          fees: selectedMultiHop.fees,
          tickSpacings: selectedMultiHop.tickSpacings
        };
      } else if (bestDirectQuote && !bestDirectQuote.amountOut.isZero()) {
        routingLog("Selected best route: direct (EXACT_INPUT)", { tickSpacing: bestDirectTickSpacing, fee: bestDirectFee, amountOut: bestDirectQuote.amountOut.toString() });
        quoteResult = {
          amountOut: bestDirectQuote.amountOut.toString(),
          amountIn: inputAmount.quotient.toString(),
          fee: bestDirectFee,
          tickSpacing: bestDirectTickSpacing,
          tokenIn: params.currencyIn,
          tokenOut: params.currencyOut,
          gasEstimate: bestDirectQuote.gasEstimate?.toString() || "200000"
        };
      }
    } else {
      const directIn = bestDirectQuote?.amountIn;
      const multiIn = selectedMultiHop?.amountIn;
      if (multiIn && (!directIn || multiIn.lt(directIn))) {
        routingLog("Selected best route: multi-hop (EXACT_OUTPUT)", { path: selectedMultiHop.path, fees: selectedMultiHop.fees, amountIn: multiIn.toString(), amountOut: amountSpecified });
        quoteResult = {
          amountOut: amountSpecified,
          amountIn: multiIn.toString(),
          fee: 0,
          tickSpacing: 0,
          tokenIn: params.currencyIn,
          tokenOut: params.currencyOut,
          gasEstimate: "200000",
          path: selectedMultiHop.path,
          fees: selectedMultiHop.fees,
          tickSpacings: selectedMultiHop.tickSpacings
        };
      } else if (bestDirectQuote) {
        routingLog("Selected best route: direct (EXACT_OUTPUT)", { tickSpacing: bestDirectTickSpacing, fee: bestDirectFee, amountIn: bestDirectQuote.amountIn.toString(), amountOut: amountSpecified });
        quoteResult = {
          amountOut: amountSpecified,
          amountIn: bestDirectQuote.amountIn.toString(),
          fee: bestDirectFee,
          tickSpacing: bestDirectTickSpacing,
          tokenIn: params.currencyIn,
          tokenOut: params.currencyOut,
          gasEstimate: bestDirectQuote.gasEstimate?.toString() || "200000"
        };
      }
    }
    if (!quoteResult) {
      routingWarn("No V3 quote found (neither direct nor multi-hop)");
      v3QuoteCache.set(cacheKey, { result: null, timestamp: Date.now() });
      return null;
    }
    enrichV3QuoteWithHopMetadata(quoteResult, v3Pools, params.currencyIn, params.currencyOut, chainId);
    routingLog("Final V3 quote", quoteResult);
    v3QuoteCache.set(cacheKey, { result: quoteResult, timestamp: Date.now() });
    return quoteResult;
  } catch (error) {
    routingError("V3 quote error", error);
    v3QuoteCache.set(cacheKey, { result: null, timestamp: Date.now() });
    return null;
  }
}
function indexV3PoolsForRouting(v3Pools) {
  const neighbors = /* @__PURE__ */ new Map();
  const poolsByPair = /* @__PURE__ */ new Map();
  const addNeighbor = (a, b) => {
    const s = neighbors.get(a) || /* @__PURE__ */ new Set();
    s.add(b);
    neighbors.set(a, s);
  };
  for (const p of v3Pools) {
    try {
      const a = p.token0.id.toLowerCase();
      const b = p.token1.id.toLowerCase();
      addNeighbor(a, b);
      addNeighbor(b, a);
      const key = a < b ? `${a}-${b}` : `${b}-${a}`;
      const list = poolsByPair.get(key) || [];
      list.push(p);
      poolsByPair.set(key, list);
    } catch (e) {
      routingWarn("Failed to index V3 pool for multi-hop candidate building", e);
    }
  }
  return { neighbors, poolsByPair };
}
function bestPoolForPair(poolsByPair, a, b) {
  const key = a < b ? `${a}-${b}` : `${b}-${a}`;
  const cands = poolsByPair.get(key) || [];
  if (cands.length === 0) return null;
  let best = null;
  for (const c of cands) {
    if (!best || parseFloat(c.totalValueLockedUSD || "0") > parseFloat(best.totalValueLockedUSD || "0")) best = c;
  }
  if (!best) return null;
  const ts = parseInt(best.tickSpacing);
  const fee = TICK_SPACING_TO_FEE[ts] ?? 3e3;
  return { tickSpacing: ts, fee };
}
function buildV3ThreeHopCandidates(inLower, outLower, neighIn, neighOut, bases, poolsByPair) {
  const out = [];
  for (let i = 0; i < bases.length; i++) {
    const mid1 = bases[i];
    if (!neighIn.has(mid1)) continue;
    for (let j = 0; j < bases.length; j++) {
      if (i === j) continue;
      const mid2 = bases[j];
      if (mid1 === mid2 || !neighOut.has(mid2)) continue;
      const hop1 = bestPoolForPair(poolsByPair, inLower, mid1);
      const hop2 = bestPoolForPair(poolsByPair, mid1, mid2);
      const hop3 = bestPoolForPair(poolsByPair, mid2, outLower);
      if (!hop1 || !hop2 || !hop3) continue;
      out.push({
        path: [inLower, mid1, mid2, outLower],
        tickSpacings: [hop1.tickSpacing, hop2.tickSpacing, hop3.tickSpacing],
        fees: [hop1.fee, hop2.fee, hop3.fee]
      });
    }
  }
  return out;
}
function buildV3MultiHopCandidates({ v3Pools, tokenIn, tokenOut, chainId }) {
  const pools = Array.isArray(v3Pools) ? v3Pools : [];
  const inLower = tokenIn.toLowerCase();
  const outLower = tokenOut.toLowerCase();
  const { neighbors, poolsByPair } = indexV3PoolsForRouting(pools);
  const neighIn = neighbors.get(inLower) || /* @__PURE__ */ new Set();
  const neighOut = neighbors.get(outLower) || /* @__PURE__ */ new Set();
  const commonIntermediaries = getCommonIntermediaries(chainId).map((x) => x.toLowerCase());
  const twoHopMids = /* @__PURE__ */ new Set();
  for (const n of neighIn) {
    if (neighOut.has(n)) twoHopMids.add(n);
  }
  for (const base of commonIntermediaries) {
    if (neighIn.has(base) && neighOut.has(base)) twoHopMids.add(base);
  }
  const routes = [];
  const seen = /* @__PURE__ */ new Set();
  const pushRoute = (path, tickSpacings, fees) => {
    const key = path.join(">") + "|" + tickSpacings.join(",");
    if (seen.has(key)) return;
    seen.add(key);
    routes.push({ path, tickSpacings, fees });
  };
  for (const mid of twoHopMids) {
    if (mid === inLower || mid === outLower) continue;
    const hop1 = bestPoolForPair(poolsByPair, inLower, mid);
    const hop2 = bestPoolForPair(poolsByPair, mid, outLower);
    if (!hop1 || !hop2) continue;
    pushRoute([inLower, mid, outLower], [hop1.tickSpacing, hop2.tickSpacing], [hop1.fee, hop2.fee]);
  }
  const threeHopBases = commonIntermediaries.filter((x) => x !== inLower && x !== outLower);
  for (const c of buildV3ThreeHopCandidates(inLower, outLower, neighIn, neighOut, threeHopBases, poolsByPair)) {
    pushRoute(c.path, c.tickSpacings, c.fees);
  }
  return routes;
}
function buildTokenLookupFromV3Pools(v3Pools, chainId) {
  const map = /* @__PURE__ */ new Map();
  for (const p of v3Pools || []) {
    try {
      for (const t of [p.token0, p.token1]) {
        if (!t?.id) continue;
        const addr = t.id.toLowerCase();
        if (map.has(addr)) continue;
        const decimals = parseInt(t.decimals);
        if (!Number.isFinite(decimals)) continue;
        map.set(addr, new Token(chainId, t.id, decimals, t.symbol, t.name));
      }
    } catch (e) {
      routingWarn("Failed to index V3 pool token for path metadata", e);
    }
  }
  return map;
}
function enrichV3QuoteWithHopMetadata(quote, v3Pools, currencyIn, currencyOut, chainId) {
  const inAddr = (currencyIn.isToken ? currencyIn.address : currencyIn.wrapped.address).toLowerCase();
  const outAddr = (currencyOut.isToken ? currencyOut.address : currencyOut.wrapped.address).toLowerCase();
  if (!quote.path || quote.path.length < 3) {
    quote.pathCurrencies = [currencyIn, currencyOut];
    quote.hopPools = [{
      fee: quote.fee,
      tickSpacing: quote.tickSpacing,
      token0: currencyIn,
      token1: currencyOut
    }];
    return;
  }
  const effectiveChainId = chainId ?? currencyIn.chainId;
  const tokenLookup = buildTokenLookupFromV3Pools(v3Pools, effectiveChainId);
  const pathCurrencies = quote.path.map((addr, i) => {
    const lower = addr.toLowerCase();
    if (i === 0 && lower === inAddr) return currencyIn;
    if (i === quote.path.length - 1 && lower === outAddr) return currencyOut;
    const t = tokenLookup.get(lower);
    if (t) return t;
    return new Token(effectiveChainId, addr, 18);
  });
  const fees = quote.fees ?? [];
  const tickSpacings = quote.tickSpacings ?? [];
  const hopPools = [];
  for (let i = 0; i < pathCurrencies.length - 1; i++) {
    hopPools.push({
      fee: fees[i] ?? quote.fee,
      tickSpacing: tickSpacings[i] ?? quote.tickSpacing,
      token0: pathCurrencies[i],
      token1: pathCurrencies[i + 1]
    });
  }
  quote.pathCurrencies = pathCurrencies;
  quote.hopPools = hopPools;
}
function encodeV3PathTickSpacing(path, tickSpacings) {
  if (!Array.isArray(path) || path.length < 2) throw new Error("encodeV3Path: invalid path");
  if (!Array.isArray(tickSpacings) || tickSpacings.length !== path.length - 1)
    throw new Error("encodeV3Path: tickSpacings length mismatch");
  const parts = [];
  parts.push(path[0].toLowerCase().replace(/^0x/, ""));
  for (let i = 0; i < tickSpacings.length; i++) {
    parts.push(tickSpacings[i].toString(16).padStart(6, "0"));
    parts.push(path[i + 1].toLowerCase().replace(/^0x/, ""));
  }
  return "0x" + parts.join("");
}
export function compareTrades(v2Trade, v3Quote, tradeType, currencyIn, currencyOut) {
  if (!v2Trade && !v3Quote) {
    return { isV3Selected: false, bestV2Trade: null, bestV3Quote: null };
  }
  if (!v2Trade) {
    return { isV3Selected: true, bestV2Trade: null, bestV3Quote: v3Quote };
  }
  if (!v3Quote) {
    return { isV3Selected: false, bestV2Trade: v2Trade, bestV3Quote: null };
  }
  if (tradeType === TradeType.EXACT_INPUT) {
    const v2Out = "outputAmount" in v2Trade ? v2Trade.outputAmount : CurrencyAmount.fromRawAmount(currencyOut, v2Trade.outputAmountRaw);
    const v3Out = CurrencyAmount.fromRawAmount(currencyOut, v3Quote.amountOut);
    const isV3Better = v3Out.greaterThan(v2Out);
    return { isV3Selected: isV3Better, bestV2Trade: v2Trade, bestV3Quote: v3Quote };
  } else {
    const v2In = "inputAmount" in v2Trade ? v2Trade.inputAmount : "inputAmountRaw" in v2Trade && v2Trade.inputAmountRaw !== void 0 ? CurrencyAmount.fromRawAmount(currencyIn, v2Trade.inputAmountRaw) : (() => {
      throw new Error("inputAmountRaw is missing from V2OnchainBestRoute");
    })();
    const v3In = CurrencyAmount.fromRawAmount(currencyIn, v3Quote.amountIn);
    const isV3Better = v3In.lessThan(v2In);
    return { isV3Selected: isV3Better, bestV2Trade: v2Trade, bestV3Quote: v3Quote };
  }
}
export function createTradeObject(comparison, tradeType, currencyIn, currencyOut, gasEstimateUSD) {
  const { isV3Selected, bestV2Trade, bestV3Quote } = comparison;
  if (!isV3Selected && bestV2Trade) {
    if ("route" in bestV2Trade) {
      const legacy = bestV2Trade;
      const routeParams = {
        v2Routes: [{ routev2: legacy.route, inputAmount: legacy.inputAmount, outputAmount: legacy.outputAmount }],
        v3Routes: [],
        mixedRoutes: [],
        tradeType,
        quoteMethod: QuoteMethod.CLIENT_SIDE_FALLBACK,
        approveInfo: { needsApprove: false }
      };
      const trade = new ClassicTrade(routeParams);
      if (!trade.routes || trade.routes.length === 0) {
        ;
        trade.routes = routeParams.v2Routes;
      }
      const routeWrapper = {
        ...legacy.route,
        pools: legacy.route.pairs,
        // Alias pairs as pools for getRoutingDiagramEntries
        protocol: Protocol.V2
      };
      const finalTrade = {
        ...trade,
        inputAmount: legacy.inputAmount,
        outputAmount: legacy.outputAmount,
        v2Routes: routeParams.v2Routes,
        swaps: [{ route: routeWrapper, inputAmount: legacy.inputAmount, outputAmount: legacy.outputAmount }],
        executionPrice: legacy.executionPrice,
        worstExecutionPrice(slippageTolerance) {
          return legacy.worstExecutionPrice(slippageTolerance);
        },
        minimumAmountOut(slippageTolerance) {
          return legacy.minimumAmountOut(slippageTolerance);
        },
        maximumAmountIn(slippageTolerance) {
          return legacy.maximumAmountIn(slippageTolerance);
        },
        inputTax: legacy.inputTax || new Percent(0),
        outputTax: legacy.outputTax || new Percent(0),
        priceImpact: legacy.priceImpact || calculatePriceImpact(currencyIn, currencyOut, legacy.inputAmount, legacy.outputAmount),
        gasUseEstimate: 25e4,
        gasUseEstimateUSD: gasEstimateUSD,
        quoteMethod: QuoteMethod.CLIENT_SIDE_FALLBACK,
        approveInfo: { needsApprove: false },
        routing: "CLASSIC"
      };
      return finalTrade;
    } else {
      const v2 = bestV2Trade;
      const inputAmt = CurrencyAmount.fromRawAmount(currencyIn, v2.inputAmountRaw);
      const outputAmt = CurrencyAmount.fromRawAmount(currencyOut, v2.outputAmountRaw);
      const routev2 = new V2Route(v2.pairs, currencyIn, currencyOut);
      const routeParams = {
        v2Routes: [{ routev2, inputAmount: inputAmt, outputAmount: outputAmt }],
        v3Routes: [],
        mixedRoutes: [],
        tradeType,
        quoteMethod: QuoteMethod.CLIENT_SIDE_FALLBACK,
        approveInfo: { needsApprove: false }
      };
      const trade = new ClassicTrade(routeParams);
      const routeWrapper = {
        ...routev2,
        pools: routev2.pairs,
        // Alias pairs as pools for getRoutingDiagramEntries
        protocol: Protocol.V2
      };
      const finalTrade = {
        ...trade,
        inputAmount: inputAmt,
        outputAmount: outputAmt,
        v2Routes: routeParams.v2Routes,
        swaps: [{ route: routeWrapper, inputAmount: inputAmt, outputAmount: outputAmt }],
        executionPrice: new Price(currencyIn, currencyOut, inputAmt.quotient.toString(), outputAmt.quotient.toString()),
        worstExecutionPrice(slippageTolerance) {
          const factor = new Percent(1).subtract(slippageTolerance);
          const worstOut = outputAmt.multiply(factor);
          return new Price(currencyIn, currencyOut, inputAmt.quotient.toString(), worstOut.quotient.toString());
        },
        minimumAmountOut(slippageTolerance) {
          const factor = new Percent(1).subtract(slippageTolerance);
          return outputAmt.multiply(factor);
        },
        maximumAmountIn() {
          return inputAmt;
        },
        inputTax: new Percent(0),
        outputTax: new Percent(0),
        priceImpact: calculatePriceImpact(currencyIn, currencyOut, inputAmt, outputAmt),
        gasUseEstimate: 25e4,
        gasUseEstimateUSD: gasEstimateUSD,
        quoteMethod: QuoteMethod.CLIENT_SIDE_FALLBACK,
        approveInfo: { needsApprove: false },
        routing: "CLASSIC"
      };
      return finalTrade;
    }
  } else if (isV3Selected && bestV3Quote) {
    const v3InputAmount = tradeType === TradeType.EXACT_INPUT ? CurrencyAmount.fromRawAmount(currencyIn, bestV3Quote.amountIn) : CurrencyAmount.fromRawAmount(currencyIn, bestV3Quote.amountIn);
    const v3OutputAmount = tradeType === TradeType.EXACT_INPUT ? CurrencyAmount.fromRawAmount(currencyOut, bestV3Quote.amountOut) : CurrencyAmount.fromRawAmount(currencyOut, bestV3Quote.amountOut);
    const executionPrice = new Price(
      currencyIn,
      // baseCurrency
      currencyOut,
      // quoteCurrency
      v3InputAmount.quotient.toString(),
      v3OutputAmount.quotient.toString()
    );
    const routePath = bestV3Quote.pathCurrencies && bestV3Quote.pathCurrencies.length >= 2 ? bestV3Quote.pathCurrencies : [currencyIn, currencyOut];
    const routePools = bestV3Quote.hopPools && bestV3Quote.hopPools.length > 0 ? bestV3Quote.hopPools : [{
      fee: bestV3Quote.fee,
      tickSpacing: bestV3Quote.tickSpacing,
      token0: currencyIn,
      token1: currencyOut
    }];
    const mockV3Route = {
      path: routePath,
      pools: routePools,
      protocol: Protocol.V3
    };
    const finalV3Trade = {
      tradeType,
      inputAmount: v3InputAmount,
      outputAmount: v3OutputAmount,
      executionPrice,
      // Add v3Routes property that transaction handler expects
      v3Routes: [{
        inputAmount: v3InputAmount,
        outputAmount: v3OutputAmount,
        path: routePath,
        pools: routePools
      }],
      swaps: [{ route: mockV3Route, inputAmount: v3InputAmount, outputAmount: v3OutputAmount }],
      gasUseEstimate: 25e4,
      gasUseEstimateUSD: gasEstimateUSD,
      quoteMethod: QuoteMethod.CLIENT_SIDE_FALLBACK,
      approveInfo: { needsApprove: false },
      routing: "CLASSIC",
      isV3: true,
      // Flag to identify V3 trades
      v3Quote: bestV3Quote,
      // Include the raw quote data
      // Add worstExecutionPrice method that trade validation expects
      worstExecutionPrice(slippageTolerance) {
        if (tradeType === TradeType.EXACT_INPUT) {
          const slippageFactor = new Percent(1).subtract(slippageTolerance);
          const worstOutputAmount = v3OutputAmount.multiply(slippageFactor);
          return new Price(
            currencyIn,
            currencyOut,
            v3InputAmount.quotient.toString(),
            worstOutputAmount.quotient.toString()
          );
        } else {
          const slippageFactor = new Percent(1).add(slippageTolerance);
          const worstInputAmount = v3InputAmount.multiply(slippageFactor);
          return new Price(
            currencyIn,
            currencyOut,
            worstInputAmount.quotient.toString(),
            v3OutputAmount.quotient.toString()
          );
        }
      },
      // Add missing methods required by analytics
      minimumAmountOut(slippageTolerance) {
        if (tradeType === TradeType.EXACT_INPUT) {
          const slippageFactor = new Percent(1).subtract(slippageTolerance);
          return v3OutputAmount.multiply(slippageFactor);
        } else {
          return v3OutputAmount;
        }
      },
      maximumAmountIn(slippageTolerance) {
        if (tradeType === TradeType.EXACT_OUTPUT) {
          const slippageFactor = new Percent(1).add(slippageTolerance);
          return v3InputAmount.multiply(slippageFactor);
        } else {
          return v3InputAmount;
        }
      },
      inputTax: new Percent(0),
      outputTax: new Percent(0),
      // Add priceImpact calculation for V3 trade
      priceImpact: calculatePriceImpact(currencyIn, currencyOut, v3InputAmount, v3OutputAmount)
    };
    return finalV3Trade;
  }
  return null;
}
